<!--
  Three-state permission gate
  ───────────────────────────
  'loading' → neutral permission-loading overlay only
  'granted' → full Listed Equipment module
  'denied'  → upgrade / locked screen
-->
<ng-container [ngSwitch]="permissionState">

  <!-- ═══════════════════════════════════════════════════════════
       STATE 1 — LOADING
  ════════════════════════════════════════════════════════════ -->
  <div class="le-perm-loading" *ngSwitchCase="'loading'">
    <div class="le-perm-spinner"></div>
  </div>


  <!-- ═══════════════════════════════════════════════════════════
       STATE 2 — ACCESS GRANTED
  ════════════════════════════════════════════════════════════ -->
  <ng-container *ngSwitchCase="'granted'">
    <div class="dashboard-view">

      <!-- ── Page Header ── -->
      <div class="listing-header-container">
        <div class="header-top">
          <div class="header-left">
            <h2>Listed Equipment</h2>
            <span class="count-badge">{{ totalItems }} vehicle{{ totalItems !== 1 ? 's' : '' }}</span>
          </div>
          <div class="header-actions">
            <div class="view-toggles">
              <button class="view-btn" [class.active]="viewMode === 'grid'" (click)="viewMode = 'grid'">
                <i class="fa-solid fa-border-all"></i>
              </button>
              <button class="view-btn" [class.active]="viewMode === 'list'" (click)="viewMode = 'list'">
                <i class="fa-solid fa-list-ul"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- ── Listing Quota Bar ── -->
        <div class="le-quota-card" *ngIf="currentPlanId !== 'free_trial'">
          <div class="le-quota-top">
            <div class="le-quota-label-group">
              <i class="fa-solid fa-layer-group"></i>
              <span class="le-quota-title">Marketplace Slots</span>
              <span class="le-quota-plan-tag" *ngIf="currentPlanName">{{ currentPlanName }}</span>
            </div>
            <div class="le-quota-numbers">
              <span class="le-quota-used" [class.le-quota-full]="freeSlots === 0">{{ usedSlots }}</span>
              <span class="le-quota-sep">/</span>
              <span class="le-quota-total">{{ totalSlots }}</span>
              <span class="le-quota-unit">slots used</span>
            </div>
          </div>
          <div class="le-quota-bar-track">
            <div class="le-quota-bar-fill"
              [style.width.%]="slotPercentage"
              [class.le-quota-bar-full]="freeSlots === 0"
              [class.le-quota-bar-warning]="slotPercentage >= 80 && freeSlots > 0">
            </div>
          </div>
          <div class="le-quota-bottom">
            <span class="le-quota-free-label">
              <ng-container *ngIf="freeSlots > 0">
                <i class="fa-solid fa-circle-check"></i> {{ freeSlots }} slot{{ freeSlots !== 1 ? 's' : '' }} available
              </ng-container>
              <ng-container *ngIf="freeSlots === 0">
                <i class="fa-solid fa-circle-exclamation"></i> All slots used — buy more to list additional vehicles
              </ng-container>
            </span>
            <button class="le-quota-buy-btn" (click)="openBuyMoreModal()" *ngIf="listingFeePerSlot > 0">
              <i class="fa-solid fa-plus"></i> Buy More Slots
            </button>
          </div>
        </div>

        <!-- ── Status Filter Pills ── -->
        <div class="le-summary-row">
          <div class="le-pill" [class.selected]="listingFilter === 'all'" (click)="setListingFilter('all')">
            All <span class="le-pill-count">{{ allEquipment.length }}</span>
          </div>
          <div class="le-pill le-pill--active" [class.selected]="listingFilter === 'active'"
            (click)="setListingFilter('active')">
            <span class="le-dot le-dot--active"></span>
            In Marketplace <span class="le-pill-count">{{ getListingCount('active') }}</span>
          </div>
          <div class="le-pill le-pill--inactive" [class.selected]="listingFilter === 'inactive'"
            (click)="setListingFilter('inactive')">
            <span class="le-dot le-dot--inactive"></span>
            Not Listed <span class="le-pill-count">{{ getListingCount('inactive') }}</span>
          </div>
        </div>

        <!-- ── Search + Filters ── -->
        <div class="header-controls">
          <div class="search-box">
            <i class="fa-solid fa-magnifying-glass"></i>
            <input type="text" placeholder="Search equipment..." [(ngModel)]="searchTerm" (input)="applySearch($event)" />
          </div>

          <div class="filter-wrapper">
            <button class="btn-filter-settings" (click)="toggleFilterPanel()"
              [class.filter-active]="activeFilterCount > 0">
              <i class="fa-solid fa-sliders"></i> Filters{{ activeFilterCount > 0 ? ' (' + activeFilterCount + ')' : '' }}
            </button>

            <!-- Backdrop -->
            <div class="le-filter-backdrop" *ngIf="showFilterPanel" (click)="closeFilterPanelWithoutApplying()"></div>

            <!-- Filter Dialog -->
            <div class="le-filter-dialog" *ngIf="showFilterPanel">
              <div class="le-filter-header">
                <div class="le-filter-header-left">
                  <div class="le-filter-title-row">
                    <i class="fa-solid fa-sliders header-icon"></i>
                    <h2>Filters</h2>
                    <span class="active-badge" *ngIf="tempActiveFilterCount > 0">
                      {{ tempActiveFilterCount }} Active
                    </span>
                  </div>
                  <p class="le-filter-subtitle">Find equipment faster using category, emirate and listing status.</p>
                </div>
                <div class="le-filter-header-right">
                  <button class="btn-clear-all" (click)="clearAllTempFilters()">
                    <i class="fa-solid fa-rotate"></i> Clear All
                  </button>
                  <button class="btn-close-dialog" (click)="closeFilterPanelWithoutApplying()">
                    <i class="fa-solid fa-xmark"></i>
                  </button>
                </div>
              </div>

              <div class="le-filter-body">
                <!-- Category -->
                <div class="le-filter-group-card">
                  <div class="le-group-header">
                    <div class="le-group-title-wrap">
                      <div class="group-icon-container"><i class="fa-solid fa-layer-group"></i></div>
                      <div class="group-meta">
                        <h3>Category</h3>
                        <span class="group-subtitle">Select one or more equipment categories</span>
                      </div>
                    </div>
                  </div>
                  <div class="le-group-chips">
                    <button class="filter-dialog-chip" [class.active]="!tempCategoryFilter" (click)="selectCategory('')">
                      All Categories
                    </button>
                    <button class="filter-dialog-chip"
                      *ngFor="let cat of categories"
                      [class.active]="tempCategoryFilter === cat.id"
                      [disabled]="!categoryCounts[cat.id]"
                      [class.disabled-chip]="!categoryCounts[cat.id]"
                      [title]="!categoryCounts[cat.id] ? 'No listed equipment in this category.' : ''"
                      (click)="selectCategory(cat.id)">
                      {{ cat.name }}
                    </button>
                  </div>
                </div>

                <!-- Emirate -->
                <div class="le-filter-group-card">
                  <div class="le-group-header">
                    <div class="le-group-title-wrap">
                      <div class="group-icon-container"><i class="fa-solid fa-location-dot"></i></div>
                      <div class="group-meta">
                        <h3>Emirate</h3>
                        <span class="group-subtitle">Choose equipment location</span>
                      </div>
                    </div>
                  </div>
                  <div class="le-group-chips">
                    <button class="filter-dialog-chip" [class.active]="!tempEmirateFilter" (click)="selectEmirate('')">
                      All Emirates
                    </button>
                    <button class="filter-dialog-chip"
                      *ngFor="let em of emirates"
                      [class.active]="tempEmirateFilter === em.name"
                      (click)="selectEmirate(em.name)">
                      {{ em.name }}
                    </button>
                  </div>
                </div>
              </div>

              <div class="le-filter-footer">
                <div class="le-footer-status" *ngIf="tempActiveFilterCount > 0">
                  {{ tempActiveFilterCount }} Filter{{ tempActiveFilterCount > 1 ? 's' : '' }} Selected
                </div>
                <div class="le-footer-status-placeholder" *ngIf="tempActiveFilterCount === 0"></div>
                <div class="le-footer-actions">
                  <button class="btn-reset-filters" (click)="resetFiltersPopup()">
                    <i class="fa-solid fa-rotate-left"></i> Reset
                  </button>
                  <button class="btn-apply-filters" (click)="applyFiltersPopup()">
                    <i class="fa-solid fa-check"></i> Apply Filters
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ══════════════════════════ GRID VIEW ══════════════════════════ -->
      <div class="listing-grid" *ngIf="!isLoading && viewMode === 'grid'">

        <div *ngIf="dataSource.length === 0 && !isLoading" class="le-empty-state">
          <i class="fa-solid fa-truck-ramp-box"></i>
          <p>No equipment found.</p>
          <span>Add equipment in Manage Equipment first.</span>
        </div>

        <div class="eq-card" *ngFor="let eq of dataSource">

          <!-- Image -->
          <div class="eq-card-img" (click)="editEquipment(eq)" style="cursor: pointer;">
            <img [src]="eq.images?.[0] || 'assets/equip-pics/default.jpeg'" [alt]="eq.name" />
          </div>

          <!-- Content -->
          <div class="eq-card-content" (click)="editEquipment(eq)" style="cursor: pointer;">
            <div class="eq-card-header">
              <div class="eq-card-title-group">
                <p class="eq-listing-subheading">
                  <i class="fa-solid fa-hashtag"></i>{{ eq['vehicleName'] || eq['name'] }}
                </p>
                <h3 [title]="eq['listingName']">{{ eq['listingName'] }}</h3>
              </div>
            </div>
            <p class="eq-manufacturer-info">
              {{ eq.model || 'Unknown' }} · {{ eq.year_of_manufacture || 'N/A' }}
            </p>
            <div class="eq-location">
              <i class="fa-solid fa-location-dot"></i>
              {{ eq.emirate || 'Unknown Location' }}
            </div>
            <div class="le-booked-notice" *ngIf="eq.listingStatus === 'booked' && eq['bookingEnd']">
              <i class="fa-solid fa-lock"></i>
              Booked until {{ eq['bookingEnd'] | date: 'dd MMM yyyy' }}
            </div>
          </div>

          <!-- Footer Actions — simplified to two buttons only -->
          <div class="eq-card-footer">
            <!-- Booked: no action possible -->
            <button class="btn-le-locked" *ngIf="eq.listingStatus === 'booked'" disabled>
              <i class="fa-solid fa-lock"></i> Active Booking
            </button>

            <!-- Show in Marketplace -->
            <button class="btn-le-show"
              *ngIf="eq.listingStatus !== 'booked' && !eq.isMarketplaceActive"
              (click)="showInMarketplace(eq)">
              <i class="fa-solid fa-store"></i>
              Show in Marketplace
            </button>

            <!-- Remove from Marketplace -->
            <button class="btn-le-remove"
              *ngIf="eq.listingStatus !== 'booked' && eq.isMarketplaceActive"
              (click)="removeFromMarketplace(eq)">
              <i class="fa-solid fa-eye-slash"></i>
              Remove from Marketplace
            </button>
          </div>
        </div>
      </div>

      <!-- ══════════════════════════ LIST VIEW ══════════════════════════ -->
      <div class="eq-list-container" *ngIf="!isLoading && viewMode === 'list'">
        <table class="eq-list-table">
          <thead>
            <tr>
              <th>Image</th>
              <th>Equipment</th>
              <th>Emirate</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngIf="dataSource.length === 0">
              <td colspan="5" style="text-align: center; padding: 30px; color: #666;">
                No equipment found.
              </td>
            </tr>
            <tr *ngFor="let eq of dataSource">
              <td (click)="editEquipment(eq)" style="cursor: pointer;">
                <div class="list-img-wrap">
                  <img [src]="eq.images?.[0] || 'assets/equip-pics/default.jpeg'" [alt]="eq.name" />
                </div>
              </td>
              <td (click)="editEquipment(eq)" style="cursor: pointer;">
                <div class="list-eq-info">
                  <span class="list-eq-name"><i class="fa-solid fa-hashtag"></i>{{ eq['vehicleName'] || eq['name'] }}</span>
                  <span class="list-eq-ref">{{ eq['listingName'] }}</span>
                  <span class="list-eq-model">{{ eq.model }} · {{ eq.year_of_manufacture }}</span>
                </div>
              </td>
              <td (click)="editEquipment(eq)" style="cursor: pointer;">
                <span class="list-eq-emirate">{{ eq.emirate }}</span>
              </td>
              <td (click)="editEquipment(eq)" style="cursor: pointer;">
                <span class="le-listing-badge le-listing-badge--inline" [ngClass]="getListingClass(eq)">
                  {{ getListingLabel(eq) }}
                </span>
              </td>
              <td>
                <div class="list-actions">
                  <!-- Booked -->
                  <button class="btn-list-action btn-list-locked" *ngIf="eq.listingStatus === 'booked'" disabled>
                    <i class="fa-solid fa-lock"></i>
                  </button>
                  <!-- Show -->
                  <button class="btn-list-action btn-list-show"
                    *ngIf="eq.listingStatus !== 'booked' && !eq.isMarketplaceActive"
                    (click)="showInMarketplace(eq)"
                    title="Show in Marketplace">
                    <i class="fa-solid fa-store"></i> Show
                  </button>
                  <!-- Remove -->
                  <button class="btn-list-action btn-list-remove"
                    *ngIf="eq.listingStatus !== 'booked' && eq.isMarketplaceActive"
                    (click)="removeFromMarketplace(eq)"
                    title="Remove from Marketplace">
                    <i class="fa-solid fa-eye-slash"></i> Remove
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Paginator -->
      <mat-paginator [length]="totalItems" [pageSize]="pageSize" [pageSizeOptions]="pageSizeOptions"
        (page)="handlePageEvent($event)" aria-label="Select page">
      </mat-paginator>

    </div>


    <!-- ══════════════════════════ BUY MORE SLOTS MODAL ══════════════════════════ -->
    <div class="bm-overlay" *ngIf="showBuyMoreModal" (click)="closeBuyMoreModal()">
      <div class="bm-modal" (click)="$event.stopPropagation()">

        <!-- Header -->
        <div class="bm-header">
          <div class="bm-header-icon">
            <i class="fa-solid fa-layer-group"></i>
          </div>
          <div class="bm-header-text">
            <h3>Add More Listing Slots</h3>
            <p>Purchase additional slots to list more vehicles in the marketplace</p>
          </div>
          <button class="bm-close" (click)="closeBuyMoreModal()" [disabled]="isBuyMoreProcessing">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <!-- Success -->
        <div class="bm-success" *ngIf="buyMorePaymentSuccess">
          <div class="bm-success-icon">
            <i class="fa-solid fa-circle-check"></i>
          </div>
          <h3>Slots Added!</h3>
          <p>{{ buyMoreCount }} new listing slot{{ buyMoreCount > 1 ? 's have' : ' has' }} been added to your account.</p>
          <p class="bm-success-sub">You can now list {{ buyMoreCount }} more vehicle{{ buyMoreCount > 1 ? 's' : '' }} in the marketplace.</p>
        </div>

        <ng-container *ngIf="!buyMorePaymentSuccess">

          <!-- Current status -->
          <div class="bm-status-row">
            <div class="bm-status-item">
              <span class="bm-status-label">Current Slots</span>
              <span class="bm-status-val">{{ totalSlots }}</span>
            </div>
            <div class="bm-status-sep"><i class="fa-solid fa-arrow-right"></i></div>
            <div class="bm-status-item bm-status-item--new">
              <span class="bm-status-label">After Purchase</span>
              <span class="bm-status-val">{{ totalSlots + buyMoreCount }}</span>
            </div>
          </div>

          <!-- Slot count picker — shown before payment init -->
          <div class="bm-picker-section" *ngIf="!_buyMoreClientSecret && !isBuyMoreInitializing">
            <label class="bm-picker-label">How many extra slots do you need?</label>
            <div class="bm-slot-grid">
              <button class="bm-slot-btn" *ngFor="let n of [1,2,3,4,5,10]"
                [class.bm-slot-btn--active]="buyMoreCount === n"
                (click)="buyMoreCount = n">
                +{{ n }}
              </button>
            </div>
            <div class="bm-custom-count">
              <label>Or enter custom amount:</label>
              <input type="number" min="1" max="50" [(ngModel)]="buyMoreCount" class="bm-count-input" />
            </div>

            <div class="bm-price-summary">
              <div class="bm-price-row">
                <span>{{ buyMoreCount }} slot{{ buyMoreCount > 1 ? 's' : '' }} × {{ listingFeePerSlot }} AED</span>
                <strong>{{ buyMoreTotal }} AED</strong>
              </div>
            </div>

            <button class="bm-proceed-btn" (click)="initiateBuyMorePayment()" [disabled]="buyMoreCount < 1">
              <i class="fa-solid fa-arrow-right"></i>
              Proceed to Payment
            </button>
          </div>

          <!-- Initializing Stripe -->
          <div class="bm-initializing" *ngIf="isBuyMoreInitializing">
            <div class="bm-spinner"></div>
            <p>Preparing payment...</p>
          </div>

          <!-- Card input — shown after payment intent created -->
          <div class="bm-card-section" *ngIf="_buyMoreClientSecret && !isBuyMoreInitializing">
            <div class="bm-payment-summary">
              <div class="bm-price-row">
                <span>{{ buyMoreCount }} listing slot{{ buyMoreCount > 1 ? 's' : '' }}</span>
                <strong>AED {{ buyMoreTotal }}</strong>
              </div>
            </div>

            <label class="bm-card-label"><i class="fa-solid fa-lock"></i> Card details</label>
            <div class="bm-card-wrap">
              <div id="buy-more-card-element"></div>
            </div>
            <p class="bm-card-note">
              <i class="fa-solid fa-shield-halved"></i> Secure payment powered by Stripe
            </p>

            <div class="bm-error" *ngIf="buyMorePaymentError">
              <i class="fa-solid fa-circle-exclamation"></i> {{ buyMorePaymentError }}
            </div>

            <button class="bm-pay-btn" (click)="payBuyMoreSlots()"
              [disabled]="isBuyMoreProcessing || isBuyMoreLocked">
              <ng-container *ngIf="!isBuyMoreProcessing">
                <i class="fa-solid fa-bolt"></i>
                Pay AED {{ buyMoreTotal }} — Add {{ buyMoreCount }} Slot{{ buyMoreCount > 1 ? 's' : '' }}
              </ng-container>
              <ng-container *ngIf="isBuyMoreProcessing">
                <i class="fa-solid fa-spinner fa-spin"></i> Processing...
              </ng-container>
            </button>

            <button class="bm-back-btn" (click)="_buyMoreClientSecret = ''; buyMorePaymentError = ''"
              *ngIf="!isBuyMoreProcessing">
              <i class="fa-solid fa-arrow-left"></i> Change slot count
            </button>
          </div>

          <!-- Error (before card init) -->
          <div class="bm-error bm-error--standalone" *ngIf="buyMorePaymentError && !_buyMoreClientSecret && !isBuyMoreInitializing">
            <i class="fa-solid fa-circle-exclamation"></i> {{ buyMorePaymentError }}
          </div>

        </ng-container>
      </div>
    </div>

  </ng-container>


  <!-- ═══════════════════════════════════════════════════════════
       STATE 3 — ACCESS DENIED
  ════════════════════════════════════════════════════════════ -->
  <ng-container *ngSwitchCase="'denied'">
    <div class="le-locked-shell">
      <div class="le-locked-card">
        <div class="le-locked-icon-wrapper">
          <div class="le-pulse-ring le-pulse-1"></div>
          <div class="le-pulse-ring le-pulse-2"></div>
          <i class="fa-solid fa-shop-lock"></i>
        </div>
        <h2>Marketplace Listings Not Available</h2>
        <p>Your current subscription plan does not include access to Listed Equipment.</p>

        <div class="le-locked-benefits">
          <div class="le-benefit-item">
            <div class="le-benefit-check"><i class="fa-solid fa-check"></i></div>
            <span>List vehicles in the marketplace</span>
          </div>
          <div class="le-benefit-item">
            <div class="le-benefit-check"><i class="fa-solid fa-check"></i></div>
            <span>Show &amp; hide listings anytime</span>
          </div>
          <div class="le-benefit-item">
            <div class="le-benefit-check"><i class="fa-solid fa-check"></i></div>
            <span>Appear in marketplace search</span>
          </div>
          <div class="le-benefit-item">
            <div class="le-benefit-check"><i class="fa-solid fa-check"></i></div>
            <span>Expand reach with paid listing slots</span>
          </div>
        </div>

        <button class="le-locked-cta" (click)="openVehiclePlanDialog()">
          Upgrade Plan
        </button>
      </div>
    </div>
  </ng-container>

</ng-container>